import { PatientWithStatsInhalerUsage } from '@/jsonapi/PatientWithStats'
import moment from 'moment-timezone'
import * as R from 'ramda'
import { fillDate } from '@/components/patient-stats/common/utils'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { removeAbbr, USER_TIME_ZONE } from '@/utils/time'
import { PatientWithEventsEvent } from '@/jsonapi/PatientWithEvents'

const yLabels = [
  '8am',
  '9am',
  '10am',
  '11am',
  '12pm',
  '1pm',
  '2pm',
  '3pm',
  '4pm',
  '5pm',
  '6pm',
  '7pm',
  '8pm',
  '9pm',
  '10pm',
  '11pm',
  '12am',
  '1am',
  '2am',
  '3am',
  '4am',
  '5am',
  '6am',
  '7am',
]

export function MaintenanceInhalerChartOptions(params: {
  timezone: string
  inhalerUsages: PatientWithStatsInhalerUsage[]
  startTimestamp: number
  endTimestamp: number
  colors: any // vuetify colors
  maintenanceInhalerEvents: PatientWithEventsEvent[]
}) {
  const { inhalerUsages, startTimestamp, endTimestamp, colors, maintenanceInhalerEvents } = params
  const timezone = params.timezone || USER_TIME_ZONE
  const groups = R.groupBy((x: any) => x.name)(inhalerUsages)
  const inhalerNames = Object.keys(groups)
  const xAxisLabels = [...fillDate(timezone, startTimestamp, endTimestamp)].map((timestamp, i) => String(timestamp))
  const hourAndAmPmFormat = 'ha'

  const scatterDatas: Array<Array<[string, string, string]>> = Object.values(groups).map(group => {
    const dataPoints = group.flatMap((usage: any) => usage.dataPoints)
    return dataPoints.map(dataPoint => {
      const x =
        moment(dataPoint.timestamp)
          .tz(timezone)
          .add(-8, 'hours')
          .startOf('day')
          .unix() * 1000
      const y = moment(dataPoint.timestamp)
        .tz(timezone)
        .startOf('hour')
        .format(hourAndAmPmFormat)
      const label = moment(dataPoint.timestamp)
        .tz(timezone)
        .format(removeAbbr(timezone, CHART_DATE_FORMAT.TIME_ABBR))

      return [String(x), y, label]
    })
  })

  const xCategory = {
    type: 'category',
    axisLine: { show: false },
    splitLine: { show: false },
    axisTick: {
      show: false,
      alignWithLabel: true,
    },
    axisLabel: {
      show: true,
      interval: xAxisLabels.length - 1,
      showMaxLabel: true,
      showMinLabel: true,
      // Force the label and value to be different
      formatter: function(value) {
        return moment(Number(value))
          .tz(timezone)
          .format(CHART_DATE_FORMAT.DATE)
      },
    },
    data: xAxisLabels,
  }

  const disableAxisOptions = {
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { show: false },
    axisLabel: { show: false },
  }

  const filledDateWithHasProblem = xAxisLabels.reduce((acc, cur) => {
    acc[cur] = false
    return acc
  }, {})
  maintenanceInhalerEvents.forEach(event => {
    filledDateWithHasProblem[
      moment(event.eventTimestamp)
        .startOf('day')
        .valueOf()
    ] = true
  })

  return {
    grid: [
      { left: '10%', top: 60, right: '10%', bottom: 60 },
      { left: '10%', top: 60, right: '10%', bottom: 53 },
    ],
    tooltip: {
      show: true,
      showContent: true,
      triggerOn: 'mousemove',
      trigger: 'item', // triggered by item is different from triggered by axis, only the selected item is triggered
      formatter(param) {
        return param.data[2] // The long-formed data is stored at position 2
      },
    },
    xAxis: [
      xCategory,
      {
        type: 'value',
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
      },
      {
        ...xCategory,
        ...disableAxisOptions,
        gridIndex: 1,
      },
    ],
    yAxis: [
      {
        type: 'category',
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: {
          formatter: function(value, index) {
            if (value === '8am') {
              return 'Day'
            }
            if (value === '12am') {
              return 'Night'
            }
          },
          interval: 3,
          textStyle: {
            padding: [10, 0, 0, 0],
          },
        },
        inverse: true,
        data: yLabels,
      },
      {
        ...disableAxisOptions,
        gridIndex: 1,
        type: 'value',
        min: 0,
        max: 48,
      },
    ],
    series: [
      ...scatterDatas.map((sd, index) => {
        return {
          type: 'scatter',
          data: sd,
          name: inhalerNames[index],
          symbolSize: 5,
          itemStyle: {
            normal: {
              color: '#5fd0d0',
            },
          },
        }
      }),
      {
        type: 'bar',
        barWidth: 9,
        xAxisIndex: 1,
        z: 0,
        animation: false,
        silent: true,
        // show bar chart as a background color
        data: [
          ...Array(16).fill({
            value: 500,
            itemStyle: {
              color: '#FFF8EE',
            },
          }),
          ...Array(8).fill({
            value: 500,
            itemStyle: {
              color: '#F3F5FC',
            },
          }),
        ],
      },
      {
        type: 'bar',
        silent: true,
        data: Object.entries(filledDateWithHasProblem).map(([key, value]) => {
          return {
            value: [key, 1],
            itemStyle: {
              color: value ? colors.burgundy : colors.teal,
            },
          }
        }),
        xAxisIndex: 2,
        yAxisIndex: 1,
      },
    ],
  }
}
